import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import Footer from '../../../components/feature/Footer';
import ScrollToTop from '../../../components/feature/ScrollToTop';
import LoadingSpinner from '../../../components/feature/LoadingSpinner';
import { fetchBlogPost, fetchBlogPostsByCategory, type BlogPost } from '../../../services/blogPosts';
import { fetchBlogSection, type BlogSection } from '../../../services/blogSection';
import Seo, { buildBreadcrumbJsonLd, SITE_URL } from '../../../components/feature/Seo';

export default function BlogDetailPage() {
  const { id } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [blogSection, setBlogSection] = useState<BlogSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPost() {
      if (!id) return;

      setLoading(true);
      const [blogPost, blogSectionData] = await Promise.all([fetchBlogPost(id), fetchBlogSection()]);
      setPost(blogPost);
      setBlogSection(blogSectionData);

      if (blogPost && blogPost.categorySlug) {
        const categoryPosts = await fetchBlogPostsByCategory(blogPost.categorySlug);
        setRelatedPosts(categoryPosts.filter((p) => p.id !== blogPost.id).slice(0, 3));
      }

      setLoading(false);
    }

    loadPost();
  }, [id]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!post) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]">
        <Seo
          title="Artikel nicht gefunden | Gerald Fasching"
          description="Der angefragte Blog-Artikel existiert nicht oder wurde verschoben."
          path={`/blog/${id ?? ''}`}
          noindex
        />
        <div className="text-center">
          <h1 className="mb-4 font-serif text-[1.75rem] font-bold text-[#1A2B3C] md:text-4xl" style={{ fontFamily: 'Lora, serif' }}>
            {blogSection?.notFoundText || 'Artikel nicht gefunden'}
          </h1>
          <Link to="/blog" className="cursor-pointer whitespace-nowrap text-[#8B1538] hover:text-[#6B0F2A]">
            {blogSection?.backToBlogText || 'Zurück zum Blog'}
          </Link>
        </div>
      </div>
    );
  }

  const postPath = `/blog/${post.slug || post.id}`;
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${SITE_URL}${postPath}#article`,
    headline: post.title,
    description: post.excerpt,
    image: post.imageUrl || undefined,
    datePublished: post.date || undefined,
    dateModified: post.date || undefined,
    author: {
      '@type': 'Person',
      name: post.author || 'Gerald Fasching',
      description: post.authorBio || undefined,
      image: post.authorImageUrl || undefined,
    },
    publisher: { '@id': `${SITE_URL}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${postPath}` },
    articleSection: post.category || undefined,
    inLanguage: 'de-AT',
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Seo
        title={`${post.title} | Gerald Fasching`}
        description={post.excerpt || `${post.title} - Artikel von ${post.author || 'Gerald Fasching'}`}
        path={postPath}
        image={post.imageUrl}
        type="article"
        articlePublishedTime={post.date}
        articleModifiedTime={post.date}
        articleAuthor={post.author}
        articleSection={post.category}
        jsonLd={[
          articleJsonLd,
          buildBreadcrumbJsonLd([
            { name: 'Start', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: postPath },
          ]),
        ]}
      />
      <article className="pb-16 pt-32">
        <div className="mx-auto mb-12 max-w-4xl px-6 lg:px-12">
          <Link
            to="/blog"
            className="mb-8 inline-flex cursor-pointer items-center whitespace-nowrap text-[#8B1538] hover:text-[#6B0F2A]"
          >
            <i className="ri-arrow-left-line mr-2 flex h-4 w-4 items-center justify-center"></i>
            {blogSection?.backToBlogText || 'Zurück zum Blog'}
          </Link>

          <div className="space-y-6">
            <div className="inline-flex items-center rounded-full bg-[#8B1538]/10 px-4 py-2">
              <span className="whitespace-nowrap text-sm font-medium text-[#8B1538]">{post.category}</span>
            </div>

            <h1 className="font-serif text-[1.75rem] font-bold leading-snug text-[#1A2B3C] md:text-[2.125rem] lg:text-[2.75rem]" style={{ fontFamily: 'Lora, serif' }}>
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <i className="ri-user-line flex h-4 w-4 items-center justify-center"></i>
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line flex h-4 w-4 items-center justify-center"></i>
                {new Date(post.date).toLocaleDateString('de-DE', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line flex h-4 w-4 items-center justify-center"></i>
                {post.readTime}
              </span>
            </div>
          </div>
        </div>

        {post.imageUrl && (
          <div className="mx-auto mb-12 max-w-6xl px-6 lg:px-12">
            <div className="relative overflow-hidden rounded-3xl" style={{ height: '500px' }}>
              <img src={post.imageUrl} alt={post.title} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-top" />
            </div>
          </div>
        )}

        <div className="mx-auto max-w-4xl px-6 lg:px-12">
          <div className="prose prose-sm max-w-none lg:prose-lg">
            <p className="mb-8 text-base font-medium leading-relaxed text-gray-700 lg:text-xl">{post.excerpt}</p>

            <div className="prose prose-sm max-w-none text-gray-700 prose-headings:font-serif prose-headings:text-[#1A2B3C] lg:prose-lg [&_h2]:text-[#1A2B3C] [&_h3]:text-[#1A2B3C]">
              <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>
                {post.content || ''}
              </ReactMarkdown>
            </div>
          </div>

          {post.authorBio && (
            <div className="mt-16 border-t border-gray-200 pt-12">
              <div className="flex items-start gap-6">
                <img
                  src={post.authorImageUrl || '/images/about/about-gerald.jpg'}
                  alt={`Autor: ${post.author}`}
                  loading="lazy"
                  decoding="async"
                  className="h-20 w-20 flex-shrink-0 rounded-full object-cover object-top"
                />
                <div>
                  <h3 className="mb-2 font-serif text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Lora, serif' }}>
                    {post.author}
                  </h3>
                  <p className="leading-relaxed text-gray-700">{post.authorBio}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </article>

      {relatedPosts.length > 0 && (
        <section className="bg-white px-6 py-16 lg:px-12">
          <div className="mx-auto max-w-7xl">
            <h2 className="mb-12 text-center font-serif text-xl font-bold text-[#1A2B3C] md:text-3xl" style={{ fontFamily: 'Lora, serif' }}>
              {blogSection?.relatedArticlesPrefix || 'Weitere Artikel zum Thema'} {post.category}
            </h2>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug || relatedPost.id}`}
                  className="group cursor-pointer overflow-hidden rounded-3xl bg-[#FAF8F5] shadow-lg transition-all duration-300 hover:shadow-xl"
                >
                  {relatedPost.imageUrl && (
                    <div className="relative overflow-hidden" style={{ height: '200px' }}>
                      <img
                        src={relatedPost.imageUrl}
                        alt={relatedPost.title}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="space-y-3 p-6">
                    <h3
                      className="line-clamp-2 font-serif text-lg font-bold text-[#1A2B3C] transition-colors group-hover:text-[#8B1538]"
                      style={{ fontFamily: 'Lora, serif' }}
                    >
                      {relatedPost.title}
                    </h3>
                    <p className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-time-line flex h-4 w-4 items-center justify-center"></i>
                      {relatedPost.readTime}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <ScrollToTop />
    </div>
  );
}
